import { Component, Input, Output, EventEmitter } from '@angular/core';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { VendorService } from '../../services/vendor.service';
import * as XLSX from 'xlsx';
import * as FileSaver from 'file-saver';

export interface GlChargeLine { account: string; name: string; value: number; }
interface MappedCreditRow {
	reference: string;
	creditDate: Date;
	dueDate: Date;
	total: number;
	discountAmount: number;
	discountDate: Date | null;
	rate: number;
	gl_account: string;
	gl_name: string;
	line_amount: number;
}
export interface CreditDraft {
	reference: string;
	creditDate: Date;
	dueDate: Date;
	discountAmount: number;
	discountDate: Date | null;
	rate: number;
	glcharges: GlChargeLine[];
}

@Component({
	selector: 'app-vendor-credit-upload',
	templateUrl: './vendor-credit-upload.component.html',
	styleUrls: ['./vendor-credit-upload.component.scss']
})
export class VendorCreditUploadComponent {
	@Input() vendor: any = null;
	@Output() newvendordata = new EventEmitter<boolean>();

	files: File[] = [];
	uploadedHeaders: string[] = [];
	uploadedRawData: any[] = [];
	columnMappings: { [header: string]: string } = {};
	availableFieldTypes: string[] = ['reference', 'credit_date', 'due_date', 'total', 'discount_amount', 'discount_date', 'rate', 'gl_account', 'gl_name', 'line_amount', 'ignore'];
	processing = false;
	uploadReady = false;
	creating = false;
	progressCurrent = 0;
	progressTotal = 0;
	successCount = 0;
	errorRows: { row: number; message: string }[] = [];
	uploadError = '';
	uploadSuccess = '';
	user: any = {};
	builtDrafts: CreditDraft[] = [];
	expandedDraftIndex: number | null = null;

	constructor(
		private vendorService: VendorService,
		private globalSearchService: GlobalSearchService
	) {
		this.globalSearchService.user.subscribe((u: any) => {
			this.user = u?.user || u || {};
		});
	}

	onDropzoneChange(event: { addedFiles: File[] }) {
		if (event.addedFiles && event.addedFiles.length) {
			this.files = event.addedFiles;
			this.processFiles();
		}
	}

	onRemove(file: File) {
		this.files = this.files.filter(f => f !== file);
		if (this.files.length === 0) {
			this.uploadedRawData = [];
			this.uploadedHeaders = [];
			this.uploadReady = false;
			this.builtDrafts = [];
		} else {
			this.processFiles();
		}
	}

	private readFileAsDataUrl(file: File): Promise<string> {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = e => resolve((e.target as FileReader).result as string);
			reader.onerror = () => reject(reader.error);
			reader.readAsDataURL(file);
		});
	}

	clearAll() {
		this.files = [];
		this.uploadedRawData = [];
		this.uploadedHeaders = [];
		this.columnMappings = {};
		this.uploadReady = false;
		this.builtDrafts = [];
		this.uploadError = '';
		this.uploadSuccess = '';
		this.errorRows = [];
		this.successCount = 0;
		this.progressCurrent = 0;
		this.progressTotal = 0;
	}

	private readFile(file: File): Promise<ArrayBuffer> {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = e => resolve((e.target as FileReader).result as ArrayBuffer);
			reader.onerror = () => reject(reader.error);
			reader.readAsArrayBuffer(file);
		});
	}

	private normalizeHeader(h: string): string {
		return (h || '').toLowerCase().trim().replace(/[\s_]+/g, '');
	}

	private autoMapColumns(): void {
		const aliases: { [key: string]: string[] } = {
			reference: ['reference', 'ref', 'refno', 'credit_no', 'creditno', 'number'],
			credit_date: ['credit_date', 'creditdate', 'cr_date', 'crdate', 'date', 'credit date'],
			due_date: ['due_date', 'duedate', 'due date'],
			total: ['total', 'amount', 'amt', 'sum', 'grand total'],
			discount_amount: ['discount_amount', 'discountamount', 'discount', 'disc amt'],
			discount_date: ['discount_date', 'discountdate', 'disc date'],
			rate: ['rate', 'exrate', 'exchange rate', 'currency rate'],
			gl_account: ['gl_account', 'glaccount', 'account', 'gl code', 'glcode'],
			gl_name: ['gl_name', 'glname', 'name', 'description', 'narrative'],
			line_amount: ['line_amount', 'lineamount', 'amount', 'value', 'line value']
		};
		const types = (['reference', 'credit_date', 'due_date', 'total', 'discount_amount', 'discount_date', 'rate', 'gl_account', 'gl_name', 'line_amount'] as const);
		this.uploadedHeaders.forEach(h => {
			const n = this.normalizeHeader(h);
			for (const t of types) {
				const list = aliases[t] || [t];
				if (list.some(a => this.normalizeHeader(a) === n || n === t.replace(/_/g, ''))) {
					this.columnMappings[h] = t;
					break;
				}
			}
		});
	}

	parseDate(val: any): Date | null {
		if (val == null || val === '') { return null; }
		if (typeof val === 'number') {
			const epoch = new Date(1899, 11, 30);
			const d = new Date(epoch.getTime() + val * 86400000);
			return isNaN(d.getTime()) ? null : d;
		}
		const d = new Date(val);
		return isNaN(d.getTime()) ? null : d;
	}

	setCreditDate(row: any, value: string) {
		row.creditDate = value ? new Date(value) : row.creditDate;
	}
	setDueDate(row: any, value: string) {
		row.dueDate = value ? new Date(value) : row.dueDate;
	}
	setDiscountDate(row: any, value: string) {
		row.discountDate = value ? new Date(value) : row.dueDate;
	}

	async processFiles() {
		if (!this.files.length) { return; }
		this.processing = true;
		this.uploadedRawData = [];
		this.uploadedHeaders = [];
		try {
			for (const file of this.files) {
				const buf = await this.readFile(file);
				const ext = (file.name || '').split('.').pop()?.toLowerCase();
				let rows: Record<string, any>[] = [];
				if (ext === 'xlsx' || ext === 'xls') {
					const workbook = XLSX.read(buf, { type: 'array' });
					const sheet = workbook.Sheets[workbook.SheetNames[0]];
					rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });
				} else if (ext === 'csv') {
					const text = new TextDecoder().decode(buf);
					const lines = text.split(/\r?\n/).filter(l => l.trim());
					if (lines.length < 2) { continue; }
					const header = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
					rows = lines.slice(1).map(line => {
						const values = line.split(',').map(v => v.trim().replace(/^"|"$/g, ''));
						const obj: Record<string, any> = {};
						header.forEach((h, i) => { obj[h] = values[i] ?? ''; });
						return obj;
					});
				}
				if (rows.length && Object.keys(rows[0]).length) {
					const headers = Object.keys(rows[0]);
					this.uploadedHeaders = [...new Set([...this.uploadedHeaders, ...headers])];
					rows.forEach(r => this.uploadedRawData.push({ ...r }));
				}
			}
			this.uploadedHeaders.forEach(h => {
				if (this.columnMappings[h] === undefined) { this.columnMappings[h] = 'ignore'; }
			});
			this.autoMapColumns();
			this.uploadReady = this.uploadedRawData.length > 0;
		} catch (e) {
			this.globalSearchService.showNotification('Error parsing file', 'danger', 'bottom', 'right');
		}
		this.processing = false;
	}

	applyMappings(): MappedCreditRow[] {
		const today = new Date();
		const defaultDue = new Date(today);
		defaultDue.setDate(defaultDue.getDate() + (this.vendor?.daysbeforedue ? parseInt(this.vendor.daysbeforedue, 10) : 30));
		const defaultRate = parseFloat(this.vendor?.exrate) || 100;
		const rows: MappedCreditRow[] = [];

		this.uploadedRawData.forEach((row) => {
			const mapped: any = {};
			Object.keys(row).forEach(header => {
				const field = this.columnMappings[header];
				if (field && field !== 'ignore') { mapped[field] = row[header]; }
			});
			const reference = (mapped['reference'] ?? '').toString().trim();
			if (!reference) { return; }

			const creditDate = this.parseDate(mapped['credit_date']) ?? today;
			const dueDate = this.parseDate(mapped['due_date']) ?? defaultDue;
			const discountDate = this.parseDate(mapped['discount_date']);
			const discountAmount = parseFloat(mapped['discount_amount']);
			const rate = parseFloat(mapped['rate']) || defaultRate;
			const total = parseFloat(mapped['total']);
			const lineAmount = parseFloat(mapped['line_amount']);
			const hasLineDetail = !isNaN(lineAmount) || (mapped['gl_account'] ?? '').toString().trim();
			if (isNaN(total) && !hasLineDetail) { return; }

			rows.push({
				reference,
				creditDate,
				dueDate,
				total: isNaN(total) ? 0 : total,
				discountAmount: isNaN(discountAmount) ? 0 : discountAmount,
				discountDate: discountDate ?? dueDate,
				rate,
				gl_account: (mapped['gl_account'] ?? '').toString().trim(),
				gl_name: (mapped['gl_name'] ?? '').toString().trim(),
				line_amount: isNaN(lineAmount) ? 0 : lineAmount
			});
		});
		return rows;
	}

	private static draftKey(r: MappedCreditRow): string {
		return (r.reference || '') + '|' + (r.creditDate?.getTime() ?? 0);
	}

	hasRequiredMappings(): boolean {
		const used = new Set<string>();
		Object.keys(this.columnMappings).forEach(header => {
			const v = this.columnMappings[header];
			if (v && v !== 'ignore') { used.add(v); }
		});
		const hasHeader = used.has('reference') && used.has('credit_date') && used.has('due_date');
		const hasTotal = used.has('total');
		const hasLineDetail = used.has('gl_account') && used.has('line_amount');
		return hasHeader && (hasTotal || hasLineDetail);
	}

	buildRows() {
		const rows = this.applyMappings();
		const byKey = new Map<string, MappedCreditRow[]>();
		rows.forEach(r => {
			const k = VendorCreditUploadComponent.draftKey(r);
			if (!byKey.has(k)) { byKey.set(k, []); }
			byKey.get(k)!.push(r);
		});
		this.builtDrafts = Array.from(byKey.entries()).map(([, group]) => {
			const first = group[0];
			let glcharges: GlChargeLine[] = group
				.filter(r => (r.gl_account || r.gl_name || r.line_amount !== 0))
				.map(r => ({ account: r.gl_account, name: r.gl_name || 'Line', value: r.line_amount }));
			if (glcharges.length === 0) {
				glcharges = [{ account: '', name: 'Total', value: first.total }];
			}
			return {
				reference: first.reference,
				creditDate: new Date(first.creditDate.getTime()),
				dueDate: new Date(first.dueDate.getTime()),
				discountAmount: first.discountAmount,
				discountDate: first.discountDate ? new Date(first.discountDate.getTime()) : first.dueDate,
				rate: first.rate,
				glcharges
			};
		});
		this.uploadError = '';
	}

	addCreditDraft() {
		const today = new Date();
		const due = new Date(today);
		due.setDate(due.getDate() + (this.vendor?.daysbeforedue ? parseInt(this.vendor.daysbeforedue, 10) : 30));
		const defaultRate = parseFloat(this.vendor?.exrate) || 100;
		this.builtDrafts.push({
			reference: '',
			creditDate: new Date(today.getTime()),
			dueDate: new Date(due.getTime()),
			discountAmount: 0,
			discountDate: new Date(due.getTime()),
			rate: defaultRate,
			glcharges: [{ account: '', name: '', value: 0 }]
		});
	}

	removeCreditDraft(index: number) {
		this.builtDrafts.splice(index, 1);
		if (this.expandedDraftIndex !== null && this.expandedDraftIndex >= this.builtDrafts.length) {
			this.expandedDraftIndex = Math.max(0, this.builtDrafts.length - 1);
		}
	}

	addGlCharge(draft: CreditDraft) {
		draft.glcharges.push({ account: '', name: '', value: 0 });
	}

	removeGlCharge(draft: CreditDraft, index: number) {
		draft.glcharges.splice(index, 1);
	}

	toggleDraftExpanded(i: number) {
		this.expandedDraftIndex = this.expandedDraftIndex === i ? null : i;
	}

	setDraftCreditDate(draft: CreditDraft, value: string) {
		draft.creditDate = value ? new Date(value) : draft.creditDate;
	}
	setDraftDueDate(draft: CreditDraft, value: string) {
		draft.dueDate = value ? new Date(value) : draft.dueDate;
	}
	setDraftDiscountDate(draft: CreditDraft, value: string) {
		draft.discountDate = value ? new Date(value) : draft.dueDate;
	}

	draftTotal(draft: CreditDraft): number {
		return draft.glcharges.reduce((s, l) => s + (Number(l.value) || 0), 0);
	}

	downloadCreditUploadTemplate() {
		const today = new Date().toISOString().slice(0, 10);
		const due = new Date();
		due.setDate(due.getDate() + 30);
		const dueStr = due.toISOString().slice(0, 10);
		const header = 'reference,credit_date,due_date,discount_amount,discount_date,rate,gl_account,gl_name,line_amount';
		const lines = [
			`CR-001,${today},${dueStr},0,${dueStr},100,6000,Return Merchandise,300.00`,
			`CR-001,${today},${dueStr},0,${dueStr},100,6100,Adjustment,50.00`,
			`CR-002,${today},${dueStr},0,${dueStr},100,6000,Credit,250.25`
		];
		const csv = [header, ...lines].join('\n');
		const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
		FileSaver.saveAs(blob, 'vendor_credit_upload_template.csv');
	}

	createCredits() {
		if (!this.vendor?.supplierid || !this.user?.userid) {
			this.uploadError = 'Vendor or user not set';
			return;
		}
		const validDrafts = this.builtDrafts.filter(d =>
			(d.reference || '').trim() &&
			d.glcharges.length > 0 &&
			d.glcharges.some(l => (l.account || '').trim() && !isNaN(Number(l.value)))
		);
		if (!validDrafts.length) {
			this.uploadError = 'No valid credits (reference and at least one GL line with account and amount per draft)';
			return;
		}
		this.creating = true;
		this.uploadError = '';
		this.uploadSuccess = '';
		this.errorRows = [];
		this.successCount = 0;
		this.progressTotal = validDrafts.length;
		this.progressCurrent = 0;

		const currcode = this.vendor.currcode || 'USD';
		const run = (index: number) => {
			if (index >= validDrafts.length) {
				this.creating = false;
				this.uploadSuccess = this.successCount + ' credit(s) created';
				if (this.successCount > 0) {
					this.globalSearchService.showNotification(this.uploadSuccess, 'success', 'bottom', 'right');
					this.newvendordata.emit(this.vendor.supplierid);
					if (this.errorRows.length === 0) {
						this.clearAll();
					}
				}
				return;
			}
			const d = validDrafts[index];
			this.progressCurrent = index + 1;
			const glcharges = d.glcharges
				.filter(l => (l.account || '').trim() && !isNaN(Number(l.value)))
				.map(l => ({ account: l.account.trim(), name: (l.name || '').trim() || l.account, value: Number(l.value) }));
			const total = glcharges.reduce((s, l) => s + l.value, 0);
			const header = {
				reference: d.reference,
				invoiceDate: d.creditDate.toISOString(),
				discountDate: (d.discountDate || d.dueDate).toISOString(),
				dueDate: d.dueDate.toISOString(),
				rate: String(d.rate),
				discountAmount: d.discountAmount,
				currcode
			};
			const data = {
				vendor: this.vendor,
				header,
				grns: [],
				glcharges,
				subtotal: total,
				taxes: 0,
				total,
				discountAmount: d.discountAmount,
				user: this.user
			};
			this.vendorService.createCredit(data).subscribe(
				(res: any) => {
					if (res?.success) {
						this.successCount++;
						if (res?.id && this.files?.length > 0) {
							const file = this.files[0];
							this.readFileAsDataUrl(file).then(content => {
								this.vendorService.uploadVendorAttachment(res.id, {
									content,
									name: file.name,
									mime: file.type || 'application/octet-stream'
								}).subscribe();
							});
						}
					} else {
						this.errorRows.push({ row: index + 1, message: res?.message || 'Create failed' });
					}
					run(index + 1);
				},
				err => {
					this.errorRows.push({ row: index + 1, message: err?.error?.message || err?.message || 'Request failed' });
					run(index + 1);
				}
			);
		};
		run(0);
	}
}
